import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';
import './kebab.css';

export interface AcaoKebab {
  rotulo: string;
  icone?: ReactNode;
  onSelect: () => void;
  perigo?: boolean;
  /** Separador acima deste item. Use para isolar a ação destrutiva. */
  separar?: boolean;
  desabilitado?: boolean;
}

/**
 * Menu de ações por linha.
 *
 * `aria-label` precisa dizer de QUE linha é o menu. Trinta botões chamados
 * "Ações" fazem a navegação por elementos do leitor de tela virar uma lista de
 * trinta itens idênticos.
 *
 * A ação destrutiva vai por último e separada — não porque fique bonito, mas
 * porque a distância reduz o clique errado quando o menu abre sob o ponteiro.
 */
export function Kebab({ rotulo, acoes }: { rotulo: string; acoes: readonly AcaoKebab[] }) {
  const [aberto, setAberto] = useState(false);
  const [acima, setAcima] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!aberto) return;
    // Decide o lado ANTES de pintar: alternar depois faria o menu saltar.
    const caixa = gatilho.current?.getBoundingClientRect();
    if (caixa) setAcima(window.innerHeight - caixa.bottom < 220);

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setAberto(false);
      gatilho.current?.focus();
    };
    const aoClicar = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('mousedown', aoClicar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('mousedown', aoClicar);
    };
  }, [aberto]);

  if (acoes.length === 0) return null;

  return (
    <div className="kebab" ref={raiz}>
      <button
        ref={gatilho}
        type="button"
        className="kebab__gatilho"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label={`Ações de ${rotulo}`}
        onClick={() => setAberto(!aberto)}
      >
        <MoreVertical size={18} aria-hidden="true" />
      </button>

      {aberto && (
        <div className="kebab__menu" role="menu" aria-label={`Ações de ${rotulo}`} data-acima={acima}>
          {acoes.map((acao) => (
            <div key={acao.rotulo}>
              {acao.separar && <div className="kebab__sep" />}
              <button
                type="button"
                role="menuitem"
                className={acao.perigo ? 'kebab__item kebab__item--perigo' : 'kebab__item'}
                disabled={acao.desabilitado}
                onClick={() => { setAberto(false); acao.onSelect(); }}
              >
                {acao.icone}
                {acao.rotulo}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
